import { Button } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  ActiveKeyspacePointer,
  Family,
  KeyspaceDirectoryEntry,
} from "@legal-500/redis-db-manager-types";
import { FAMILIES } from "@legal-500/redis-db-manager-types";
import { type DbManagerKeyspace, dbManagerService } from "../../../api/dbManager.service.js";
import { ROUTES } from "../../../routes.js";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { CreateKeyspaceModal } from "./CreateKeyspaceModal.js";
import { ConfirmActionModal } from "./ConfirmActionModal.js";
import { DiscardModal } from "./DiscardModal.js";
import { LogTailDrawer } from "./LogTailDrawer.js";
import { MakeActiveModal } from "./MakeActiveModal.js";
import { PreviewLinkModal } from "./PreviewLinkModal.js";
import { RemoveKeyspaceModal } from "./RemoveKeyspaceModal.js";
import { SlotCard, type SlotCardActions } from "./SlotCard.js";
import { SlotDetailModal } from "./SlotDetailModal.js";
import { TriggerSyncModal } from "./TriggerSyncModal.js";
import { UserLabel } from "./UserLabel.js";
import { computeSlotStatus, formatTimestamp } from "./slotStatus.js";
import { targetedFlagsUnknownMessage } from "./SlotTargetedFlagList.js";
import { useSlotTargetedFlags } from "./useSlotTargetedFlags.js";
import styles from "./DbManager.module.css";

const POLL_INTERVAL = 5000;

type Toast = { id: number; type: "success" | "error"; message: string };

type ModalState =
  | { kind: "none" }
  | {
      kind: "trigger-sync";
      keyspace?: string;
      family: Family;
      initialPageTypes?: string[];
      initialClearAllData?: boolean;
    }
  | { kind: "make-active"; entry: KeyspaceDirectoryEntry; missingPageTypes: string[] }
  | { kind: "preview-link"; label: string; family: Family }
  | { kind: "create-keyspace"; family: Family }
  | { kind: "discard"; keyspace: string; family: Family }
  | { kind: "remove"; keyspace: string; family: Family }
  | { kind: "detail"; entry: KeyspaceDirectoryEntry }
  | { kind: "log-tail"; keyspace: string; runId: string }
  | { kind: "cancel-sync"; keyspace: string };

type KeyspaceMemory = { keyCount: number; datasetBytes: number };

function DbManagerPage() {
  const navigate = useNavigate();
  const [keyspaces, setKeyspaces] = useState<DbManagerKeyspace[]>([]);
  const [keyspaceMemory, setKeyspaceMemory] = useState<Record<string, KeyspaceMemory>>({});
  const [activeKeyspaces, setActiveKeyspaces] = useState<
    Record<Family, ActiveKeyspacePointer | null>
  >(
    () =>
      Object.fromEntries(FAMILIES.map(family => [family, null])) as Record<
        Family,
        ActiveKeyspacePointer | null
      >,
  );
  const [pageTypes, setPageTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [modal, setModal] = useState<ModalState>({ kind: "none" });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);
  const targetedFlags = useSlotTargetedFlags();

  const pushToast = useCallback((type: Toast["type"], message: string) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  }, []);

  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [keyspacesResponse, types] = await Promise.all([
        dbManagerService.listKeyspaces(),
        dbManagerService.listPageTypes().catch(() => [] as string[]),
      ]);
      setKeyspaces(keyspacesResponse.keyspaces);
      setActiveKeyspaces(keyspacesResponse.activeKeyspaces);
      if (types.length > 0) setPageTypes(types);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchMemory = useCallback(async () => {
    try {
      const memoryResponse = await dbManagerService.getKeyspaceMemory();
      const map: Record<string, KeyspaceMemory> = {};
      for (const entry of memoryResponse.keyspaces) {
        map[entry.keyspace] = {
          keyCount: entry.keyCount,
          datasetBytes: entry.datasetBytes,
        };
      }
      setKeyspaceMemory(map);
    } catch {
      // Best-effort telemetry only.
    }
  }, []);

  useEffect(() => {
    void fetchData();
    void fetchMemory();
  }, [fetchData, fetchMemory]);

  useEffect(() => {
    const interval = setInterval(() => {
      void fetchData(true);
      void fetchMemory();
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData, fetchMemory]);

  const closeModal = useCallback(() => setModal({ kind: "none" }), []);

  const isActive = useCallback(
    (entry: KeyspaceDirectoryEntry): boolean => {
      const pointer = activeKeyspaces[entry.family];
      return pointer?.mode === "slot" && pointer.slot === entry.keyspace;
    },
    [activeKeyspaces],
  );

  const handleTriggerSync = useCallback(
    (keyspace: string) => {
      const entry = keyspaces.find(k => k.keyspace === keyspace);
      if (!entry) return;
      setModal({ kind: "trigger-sync", keyspace, family: entry.family });
    },
    [keyspaces],
  );

  const handleRetrySync = useCallback(
    (keyspace: string) => {
      const entry = keyspaces.find(k => k.keyspace === keyspace);
      if (!entry) return;
      const failedTypes = entry.pageTypes.map(pt => pt.name);
      setModal({
        kind: "trigger-sync",
        keyspace,
        family: entry.family,
        initialPageTypes: failedTypes.length > 0 ? failedTypes : undefined,
      });
    },
    [keyspaces],
  );

  const handleMakeActive = useCallback(
    (keyspace: string) => {
      const entry = keyspaces.find(k => k.keyspace === keyspace);
      if (!entry) return;
      const { missingPageTypes } = computeSlotStatus(entry, false, pageTypes);
      setModal({ kind: "make-active", entry, missingPageTypes });
    },
    [keyspaces, pageTypes],
  );

  const handlePreviewLink = useCallback(
    (keyspace: string) => {
      const entry = keyspaces.find(k => k.keyspace === keyspace);
      if (!entry) return;
      setModal({ kind: "preview-link", label: keyspace, family: entry.family });
    },
    [keyspaces],
  );

  const handleDiscard = useCallback(
    (keyspace: string) => {
      const entry = keyspaces.find(k => k.keyspace === keyspace);
      if (!entry) return;
      setModal({ kind: "discard", keyspace, family: entry.family });
    },
    [keyspaces],
  );

  const handleRemove = useCallback(
    (keyspace: string) => {
      const entry = keyspaces.find(k => k.keyspace === keyspace);
      if (!entry) return;
      setModal({ kind: "remove", keyspace, family: entry.family });
    },
    [keyspaces],
  );

  const handleViewProgress = useCallback(
    (keyspace: string) => {
      const entry = keyspaces.find(k => k.keyspace === keyspace);
      if (!entry?.runId) {
        pushToast(
          "error",
          `No run ID recorded for keyspace ${keyspace}. Logs are only retained for the most recent run.`,
        );
        return;
      }
      setModal({ kind: "log-tail", keyspace, runId: entry.runId });
    },
    [keyspaces, pushToast],
  );

  const handleViewDetail = useCallback(
    (keyspace: string) => {
      const entry = keyspaces.find(k => k.keyspace === keyspace);
      navigate(ROUTES.l500Data.redisLoadingKeyspace(keyspace, entry?.family));
    },
    [navigate, keyspaces],
  );

  const handleViewFailures = useCallback(
    (keyspace: string) => {
      const entry = keyspaces.find(k => k.keyspace === keyspace);
      if (!entry) return;
      setModal({ kind: "detail", entry });
    },
    [keyspaces],
  );

  const handleCancelSync = useCallback(async (keyspace: string) => {
    setModal({ kind: "cancel-sync", keyspace });
  }, []);

  const cardActions = useMemo<SlotCardActions>(
    () => ({
      onTriggerSync: handleTriggerSync,
      onMakeActive: handleMakeActive,
      onPreviewLink: handlePreviewLink,
      onDiscard: handleDiscard,
      onRemove: handleRemove,
      onViewProgress: handleViewProgress,
      onViewDetail: handleViewDetail,
      onViewFailures: handleViewFailures,
      onRetrySync: handleRetrySync,
      onCancelSync: handleCancelSync,
    }),
    [
      handleTriggerSync,
      handleMakeActive,
      handlePreviewLink,
      handleDiscard,
      handleRemove,
      handleViewProgress,
      handleViewDetail,
      handleViewFailures,
      handleRetrySync,
      handleCancelSync,
    ],
  );

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) {
      return <UnauthorizedView />;
    }
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="legal500.com Data"
        subtitle="Manage BFF Redis slots: trigger syncs, cut over, generate preview links."
      />

      <ActiveKeyspaceHeader activeKeyspace={activeKeyspaces.core} />

      {loading && <LoadingState message="Loading slots..." />}

      {!loading && error && (
        <EmptyState
          variant="error"
          title="Failed to load slots"
          message={error.message}
          action={{ label: "Retry", onClick: () => fetchData() }}
        />
      )}

      {!loading && !error && (
        <>
          {targetedFlags.error && (
            <div className={styles.warningBanner} role="status">
              {targetedFlagsUnknownMessage({ loading: false, error: targetedFlags.error })}
            </div>
          )}
          {FAMILIES.map(family => {
            const familyEntries = keyspaces.filter(entry => entry.family === family);
            const familyPointer = activeKeyspaces[family];
            const familyActive =
              familyPointer?.mode === "slot" &&
              familyEntries.some(entry => entry.keyspace === familyPointer.slot);
            return (
              <section key={family} className={styles.slotDetailSection}>
                <div className={styles.cardHeader}>
                  <h2 className={styles.slotDetailHeading}>{family}</h2>
                  <div className={styles.headerActions}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setModal({ kind: "create-keyspace", family })}
                    >
                      Create slot
                    </Button>
                  </div>
                </div>
                {!familyActive && (
                  <p className={styles.slotDetailEmpty}>
                    Currently serving legacy data for {family}.
                  </p>
                )}
                <div className={styles.grid}>
                  {familyEntries.map(entry => (
                    <SlotCard
                      key={entry.keyspace}
                      entry={entry}
                      isActive={isActive(entry)}
                      allPageTypes={pageTypes}
                      memory={keyspaceMemory[entry.keyspace]}
                      targetedFlagKeys={targetedFlags.flagsFor(entry.keyspace).flagKeys}
                      actions={cardActions}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}

      {modal.kind === "trigger-sync" && (
        <TriggerSyncModal
          isOpen
          initialKeyspace={modal.keyspace ?? ""}
          family={modal.family}
          initialPageTypes={modal.initialPageTypes}
          initialClearAllData={modal.initialClearAllData}
          allKeyspaces={keyspaces}
          onClose={closeModal}
          onSubmitted={runId => {
            closeModal();
            pushToast("success", `Sync triggered (run ${runId})`);
            void fetchData(true);
            void fetchMemory();
          }}
        />
      )}

      {modal.kind === "create-keyspace" && (
        <CreateKeyspaceModal
          isOpen
          family={modal.family}
          onClose={closeModal}
          onCreated={keyspace => {
            closeModal();
            pushToast("success", `Slot ${keyspace} created.`);
            void fetchData(true);
            void fetchMemory();
          }}
        />
      )}

      {modal.kind === "make-active" && (
        <MakeActiveModal
          isOpen
          targetEntry={modal.entry}
          currentActive={activeKeyspaces[modal.entry.family]}
          missingPageTypes={modal.missingPageTypes}
          targetedFlags={targetedFlags.flagsFor(modal.entry.keyspace)}
          onClose={closeModal}
          onActivated={(newActive, warning) => {
            setActiveKeyspaces(prev => ({ ...prev, [modal.entry.family]: newActive }));
            closeModal();
            pushToast(
              "success",
              warning
                ? `${newActive.mode === "slot" ? newActive.slot : "legacy"} is now active. ${warning}`
                : `${newActive.mode === "slot" ? newActive.slot : "legacy"} is now active.`,
            );
            void fetchData(true);
            void fetchMemory();
          }}
        />
      )}

      {modal.kind === "preview-link" && (
        <PreviewLinkModal isOpen label={modal.label} family={modal.family} onClose={closeModal} />
      )}

      {modal.kind === "discard" && (
        <DiscardModal
          isOpen
          keyspace={modal.keyspace}
          family={modal.family}
          onClose={closeModal}
          onDiscarded={() => {
            closeModal();
            pushToast("success", `Slot ${modal.keyspace} discarded.`);
            void fetchData(true);
            void fetchMemory();
          }}
        />
      )}

      {modal.kind === "remove" && (
        <RemoveKeyspaceModal
          isOpen
          keyspace={modal.keyspace}
          family={modal.family}
          onClose={closeModal}
          onRemoved={() => {
            closeModal();
            pushToast("success", `Slot ${modal.keyspace} removed.`);
            void fetchData(true);
            void fetchMemory();
          }}
        />
      )}

      {modal.kind === "cancel-sync" && (
        <ConfirmActionModal
          isOpen
          title={`Cancel sync for ${modal.keyspace}`}
          body={
            <p>
              Stop the running sync for slot <code>{modal.keyspace}</code>. Any progress recorded so
              far will remain visible.
            </p>
          }
          confirmLabel="Cancel sync"
          pendingLabel="Cancelling..."
          onClose={closeModal}
          onConfirm={async () => {
            await dbManagerService.cancelSync(modal.keyspace);
            closeModal();
            pushToast("success", `Cancellation requested for slot ${modal.keyspace}.`);
            void fetchData(true);
            void fetchMemory();
          }}
        />
      )}

      {modal.kind === "detail" && (
        <SlotDetailModal
          isOpen
          entry={modal.entry}
          targetedFlags={targetedFlags.flagsFor(modal.entry.keyspace)}
          onClose={closeModal}
        />
      )}

      {modal.kind === "log-tail" && (
        <LogTailDrawer isOpen keyspace={modal.keyspace} runId={modal.runId} onClose={closeModal} />
      )}

      <div className={styles.toastContainer}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`${styles.toast} ${
              toast.type === "success" ? styles.toastSuccess : styles.toastError
            }`}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}

function ActiveKeyspaceHeader({
  activeKeyspace,
}: {
  activeKeyspace: ActiveKeyspacePointer | null;
}) {
  if (!activeKeyspace || activeKeyspace.mode === "legacy") {
    return (
      <div className={styles.activeHeader}>
        <span className={styles.activeHeaderTitle}>Active routing</span>
        <span className={styles.activeHeaderValue}>Legacy</span>
      </div>
    );
  }

  return (
    <div className={styles.activeHeader} data-testid="active-slot-header">
      <span className={styles.activeHeaderTitle}>Active slot</span>
      <span className={styles.activeHeaderValue}>
        <code>{activeKeyspace.slot}</code>
      </span>
      <span className={styles.activeHeaderMeta}>
        Cut over {formatTimestamp(activeKeyspace.sinceMs)} by{" "}
        <UserLabel value={activeKeyspace.swappedBy} />
      </span>
    </div>
  );
}

export default DbManagerPage;
